// ─────────────────────────────────────────────────────────────────
// Glass-i — Supplier Payment API client (Stage 6P.7).
//
// Thin axios wrappers around:
//   /api/erp/supplier-payments/*  (Supplier Payment + Payment Voucher
//                                  Integration — 6P.7)
//
// Mirrors the purchaseAccountingApi shape — the `unwrap` helper handles
// both the {data:{data:...}} envelope used by most list/create endpoints
// and the bare {data:...} shape used by helper / status / preview routes.
//
// Backend is the source of truth for ALL payment math:
//   ledger code resolution (payment_account + supplier_payable chain),
//   payable balance computation (purchase_bill outstanding + advance/
//   on-account credit), debit/credit balance enforcement, duplicate-
//   block decisions, voucherPosting integration.
// Frontend mirrors for instant UX but never wins.
// ─────────────────────────────────────────────────────────────────

import client from '../../../api/client';

const SP_BASE = '/api/erp/supplier-payments';

function unwrap(resp) {
  return resp && resp.data && resp.data.data !== undefined
    ? resp.data.data
    : resp && resp.data;
}

export const supplierPaymentApi = {
  // ── Payment CRUD ──────────────────────────────────────────────
  listPayments:        (params = {}) =>
    client.get(`${SP_BASE}`, { params }).then(unwrap),
  getPayment:          (id) =>
    client.get(`${SP_BASE}/${encodeURIComponent(id)}`).then(unwrap),
  createPayment:       (payload = {}) =>
    client.post(`${SP_BASE}`, payload).then(unwrap),
  updatePayment:       (id, payload = {}) =>
    client.put(`${SP_BASE}/${encodeURIComponent(id)}`, payload).then(unwrap),
  removePayment:       (id) =>
    client.delete(`${SP_BASE}/${encodeURIComponent(id)}`).then(unwrap),

  // ── Preview / post / cancel ──────────────────────────────────
  previewPosting:      (id) =>
    client.post(`${SP_BASE}/preview`, { id }).then(unwrap),
  postPayment:         (id, payload = {}) =>
    client
      .post(`${SP_BASE}/${encodeURIComponent(id)}/post`, payload)
      .then(unwrap),
  cancelPayment:       (id, reason) =>
    client
      .post(`${SP_BASE}/${encodeURIComponent(id)}/cancel`, { reason })
      .then(unwrap),

  // ── Payables & balances ───────────────────────────────────────
  listPayables:        (params = {}) =>
    client.get(`${SP_BASE}/payables`, { params }).then(unwrap),
  getSupplierPayableBalance: (supplierId, params = {}) =>
    client.get(`${SP_BASE}/payables/${encodeURIComponent(supplierId)}`, { params }).then(unwrap),
  getBillOutstandingBalance: (billId, params = {}) =>
    client.get(`${SP_BASE}/bill-balance/${encodeURIComponent(billId)}`, { params }).then(unwrap),
  getPostingOutstandingBalance: (postingId, params = {}) =>
    client.get(`${SP_BASE}/posting-balance/${encodeURIComponent(postingId)}`, { params }).then(unwrap),

  // ── Register & history ────────────────────────────────────────
  listRegister:        (params = {}) =>
    client.get(`${SP_BASE}/register`, { params }).then(unwrap),
  getSupplierHistory:  (supplierId, params = {}) =>
    client.get(`${SP_BASE}/history/${encodeURIComponent(supplierId)}`, { params }).then(unwrap)
};

export default { supplierPaymentApi };
